import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import {
  Form,
  Input,
  Select,
  Upload,
  Button,
  Collapse,
  Typography,
  Checkbox,
  Spin,
  message
} from "antd";
import UploadOutlined from "@ant-design/icons/UploadOutlined";
import { saveBuyerData } from '../../../../../api/requests';
import style from './purpose.module.scss';
const { Panel } = Collapse;
const { Text } = Typography;

const FILE_TYPES = {
  AGREEMENT: 'agreement',
  IDENTITY: 'identity',
  INITIAL_AGREEMENT: 'initialAgreement'
};

const exampleDocuments = {
  1: '/documents/sale.docx',
  2: '/documents/delivery.docx',
};

const VehiclePurposeForm = ({ carID, buyer, carPurpose, documents, contractAcknowledged, acknowledgedDate }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [purpose, setPurpose] = useState(carPurpose);
  const [isFormValid, setIsFormValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [signatureTimestamp, setSignatureTimestamp] = useState(acknowledgedDate);
  const [contractSigned, setContractSigned] = useState(contractAcknowledged);

  const finalAgreement = useMemo(() => documents?.find((doc) => doc.finance_field === FILE_TYPES.INITIAL_AGREEMENT)?.finance_field, [documents]);

  const updatedFiles = useMemo(() => {
    return documents.length ? documents.map(document => ({
      ...document,
      file_location: document.file_location.replace("public", "api")
    })) : [];
  }, [documents]);

  useEffect(() => {
    setLoading(true);
    const checkBuyer = () => {
      if (buyer) {
        form.setFieldsValue(buyer);
        setIsReadOnly(true);
        setIsFormValid(false);
      } else {
        setIsReadOnly(false);
        form.resetFields();
      }
      setLoading(false);
    }
    checkBuyer();
  }, [buyer, form]);

  useEffect(() => {
    if (finalAgreement && !contractAcknowledged && contractSigned) {
      setIsFormValid(true);
    }
  }, [documents, contractAcknowledged, finalAgreement, contractSigned])

  const handlePurposeChange = (value) => {
    setPurpose(value);
    form.resetFields([
      "fullName",
      "phone",
      "buyerEmail",
      "buyerID",
      "country",
      "address",
      "buyerDocuments",
      "contractAcknowledged",
      "agreement"
    ]);
  };

  const handleAcknowledgmentChange = async (e) => {
    const checked = e.target.checked;
    setContractSigned(checked);

    if (checked && !signatureTimestamp) {
      const timestamp = new Date().toISOString();
      setSignatureTimestamp(timestamp);
    }
  };

  const normFile = (e) => {
    if (Array.isArray(e)) return e;
    return e?.fileList;
  };

  const handleFieldsChange = () => {
    const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
    const allFields = form.getFieldsValue();

    const fieldNames = Object.keys(allFields).filter(name => name !== 'vehiclePurpose');

    const touched = form.isFieldsTouched(fieldNames, true);
    if (!contractSigned) {
      setIsFormValid(touched && !hasErrors);
    }
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file); // base64 with MIME type
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const mapUploadFile = async (files, fieldName) => {
    if (!Array.isArray(files) || files.length === 0) return null;

    const f = files[0];
    const file = f.originFileObj;
    const base64 = file ? await toBase64(file) : "";

    return {
      file: base64,
      fileName: f.name,
      field: fieldName,
    };
  };

  const handleFinish = async (values) => {
    const [identity, agreement] = await Promise.all([
      mapUploadFile(values.buyerDocuments, FILE_TYPES.IDENTITY),
      mapUploadFile(values.agreement, FILE_TYPES.AGREEMENT),
    ]);

    const payload = {
      buyer: { ...values },
      documents: { identity, agreement },
    };
    delete payload.buyer.buyerDocuments;
    delete payload.buyer.agreement;
    delete payload.buyer.vehiclePurpose;
    payload.buyer.contractAcknowledged = contractSigned;
    payload.buyer.acknowledgedDate = signatureTimestamp;

    if (contractAcknowledged) {
      setContractSigned(true);
    }

    try {
      const response = await saveBuyerData(payload, carID);
      if (response?.status === 200) {
        message.success(t("data_saved_success"));
        setIsReadOnly(true);
      } else {
        message.error(t("data_saved_fail"));
      }
    } catch (error) {
      message.error(t("data_saved_fail"));
      console.error(error);
    }
  };

  const buyerUploadedDocument = useMemo(() => {
    return updatedFiles?.[0] ? [
      {
        uid: updatedFiles[0].id.toString(),
        name: updatedFiles[0].name,
        status: 'done',
        url: `/${updatedFiles[0].file_location}`,
      }
    ] : []
  }, [updatedFiles]);

  const initialAgreementUploadedDocument = useMemo(() => {
    return updatedFiles?.[1] ? [
      {
        uid: updatedFiles[1].id.toString(),
        name: updatedFiles[1].name,
        status: 'done',
        url: `/${updatedFiles[1].file_location}`,
      }
    ] : []
  }, [updatedFiles]);

  useEffect(() => {
    if (buyerUploadedDocument && initialAgreementUploadedDocument) {
      form.setFieldsValue({ buyerDocuments: buyerUploadedDocument });
      form.setFieldsValue({ agreement: initialAgreementUploadedDocument });
    }
  }, [buyerUploadedDocument, initialAgreementUploadedDocument, form]);

  if (loading) return <Spin />;

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onFieldsChange={handleFieldsChange}
      className={style.purposeForm}
      initialValues={{
        vehiclePurpose: `${purpose}`,
        buyerDocuments: buyerUploadedDocument,
        agreement: initialAgreementUploadedDocument
      }}
    >
      <Form.Item
        name="vehiclePurpose"
        rules={[{ required: true, message: t('car_bought_for') }]}
        className={style.purposeSelectWrapper}
      >
        <Select placeholder={t('car_bought_for')} onChange={handlePurposeChange} className={style.purposeSelect} disabled={isReadOnly}>
          <Select.Option value="1">{t('sell')}</Select.Option>
          <Select.Option value="2">{t('freight_forwarder_option')}</Select.Option>
        </Select>
      </Form.Item>

      {purpose && (
        <Collapse defaultActiveKey={["1", "2"]} className={style.purposeCollapse}>
          <Panel header={t("buyer_information_panel")} key="1">
            <Form.Item
              label={t("placeholder_name")}
              name="fullName"
              rules={[{ required: true, message: t("full_name_required") }]}
            >
              <Input disabled={isReadOnly} />
            </Form.Item>

            <Form.Item
              label={t("phone_label")}
              name="phone"
              rules={[{ required: true, message: t("phone_required") }]}
            >
              <Input type="tel" disabled={isReadOnly} />
            </Form.Item>

            <Form.Item
              label={t("placeholder_email")}
              name="email"
              rules={[
                { type: 'email', message: t("email_invalid") },
                { required: true, message: t("email_required") }
              ]}
            >
              <Input type="email" disabled={isReadOnly} />
            </Form.Item>

            <Form.Item
              label={t("id_type_label")}
              name="idType"
              rules={[{ required: true, message: t("id_type_is_required") }]}
            >
              <Select placeholder={t("id_type_placeholder")} disabled={isReadOnly}>
                <Select.Option value="passport">{t("passport_option")}</Select.Option>
                <Select.Option value="id">{t("license_option")}</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              label={t("id_number_label")}
              name="idNumber"
              rules={[{ required: true, message: t("id_number_required") }]}
            >
              <Input disabled={isReadOnly} />
            </Form.Item>

            <Form.Item
              label={t("placeholder_country")}
              name="country"
              rules={[{ required: true, message: t("country_required") }]}
            >
              <Input disabled={isReadOnly} />
            </Form.Item>

            <Form.Item
              label={t("address_label")}
              name="address"
              rules={[{ required: true, message: t("address_required") }]}
            >
              <Input disabled={isReadOnly} />
            </Form.Item>

            <Form.Item
              label={t("buyer_documents_label")}
              name="buyerDocuments"
              valuePropName="fileList"
              getValueFromEvent={normFile}
              rules={[{ required: true, message: t("buyer_documents_required") }]}
            >
              <Upload beforeUpload={() => false} showUploadList multiple={false} maxCount={1} disabled={isReadOnly} className={style.upload}>
                <Button icon={<UploadOutlined />}>{t("upload_button_text")}</Button>
              </Upload>
            </Form.Item>

            <Form.Item
              label={
                purpose === 1
                  ? t("purchase_sale_agreement_label")
                  : t("freight_forwarding_agreement_label")
              }
              name="agreement"
              rules={[{ required: true, message: t("agreement_required") }]}
              valuePropName="fileList"
              getValueFromEvent={normFile}
            >
              <Upload accept=".pdf,.jpg,.png" beforeUpload={() => false} showUploadList maxCount={1} disabled={isReadOnly} className={style.upload}>
                <Button icon={<UploadOutlined />}>{t("upload_agreement_button_text")}</Button>
              </Upload>
            </Form.Item>
            {purpose && (
              <div style={{ marginBottom: 12 }}>
                <a
                  href={exampleDocuments[purpose]}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className={style.exampleLink}
                >
                  {t("download_example_document")}
                </a>
              </div>
            )}
          </Panel>

          <Panel header={t('autobuy_official_contract')} key="2">
            {finalAgreement ? (
              <div style={{ marginBottom: 12 }}>
                <a
                  href={`/${updatedFiles.find(doc => doc.finance_field === FILE_TYPES.INITIAL_AGREEMENT).file_location}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("preview_contract")}
                </a>
              </div>
            ) : (
              <Text type="secondary">{t("no_contract_uploaded")}</Text>
            )}

            {!contractAcknowledged && finalAgreement && (
              <Form.Item
                name="contractAcknowledged"
                valuePropName="checked"
                rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error(t("acknowledge_required"))) }]}
              >
                <Checkbox onChange={(e) => handleAcknowledgmentChange(e)}>
                  {t("acknowledge_label")}
                </Checkbox>
              </Form.Item>
            )}

            {contractSigned && signatureTimestamp && (
              <div style={{ marginTop: 12 }}>
                <Text type="success">
                  {t("already_signed")}
                  {' '}
                  {new Date(signatureTimestamp).toLocaleString()}
                </Text>
              </div>
            )}
          </Panel>
        </Collapse>
      )}

      {purpose && (
        <Form.Item style={{ marginTop: 24 }}>
          <Button type="primary" htmlType="submit" disabled={!isFormValid}>
            {t("save_changes")}
          </Button>
        </Form.Item>
      )}
    </Form>
  );
};

export default VehiclePurposeForm;